'use client';

import React, { ReactElement, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import {
  AtivityItemMode,
  NotificationActivityEnrichedRecord,
  NotificationsPortalContext,
} from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { getBoxFileUrl, openTaskDetailedInDashboard } from '@nx-frontend/shared/utils';
import { ActivityContext } from './activity-context';
import { ActivityIcon } from './activity-icon';
import { ActivityParent } from './activity-parent';
import { ActivityRoot } from './activity-root';
import { ActivityTitle } from './activity-title';
import { readActivityEvent } from './store';
import { getActivityParams } from './utils/get-activity-params';
import { getUserName } from './utils/get-user-name';

const activityIconBgColorValues = {
  edit: '#FFF7ED',
  comment: '#FEFCE8',
  create: '#FAF5FF',
  mention: '#FDF2F8',
  message: '#F0F9FF',
  move: '#F0FDF4',
  remove: '#F9FAFB',
  reply: '#FEFCE8',
  share: '#F5F3FF',
  publish: '#FAF5FF',
  unpublish: '#F9FAFB',
};

const activityIconBorderColorValues = {
  edit: '#FFEDD5',
  comment: '#FEF9C3',
  create: '#F3E8FF',
  mention: '#FCE7F3',
  message: '#E0F2FE',
  move: '#DCFCE7',
  remove: '#F3F4F6',
  reply: '#FEF9C3',
  share: '#EDE9FE',
  publish: '#F3E8FF',
  unpublish: '#F3F4F6',
};

const activityIconColorValues = {
  edit: 'text-orange-600 w-3 h-3',
  comment: 'text-yellow-600 w-3 h-3',
  create: 'text-purple-600 w-3 h-3',
  mention: 'text-pink-600 w-3 h-3',
  message: 'text-sky-600 w-3 h-3',
  move: 'text-green-600 w-3 h-3',
  remove: 'text-gray-600 w-3 h-3',
  reply: 'text-yellow-600 w-3 h-3',
  share: 'text-violet-600 w-3 h-3',
  publish: 'text-purple-600 w-3 h-3',
  unpublish: 'text-gray-600 w-3 h-3',
};

const activityIconValues = {
  edit: IconsMap.EDIT,
  comment: IconsMap.CIRCLE_COMMENT,
  create: IconsMap.CREATE_PAGE,
  mention: IconsMap.MENTION,
  message: IconsMap.CHAT_1,
  move: IconsMap.MOVE_TO,
  remove: IconsMap.MINUS,
  reply: IconsMap.ARROW_REPLY,
  share: IconsMap.SHARE,
  createFolder: IconsMap.FOLDER_PLUS,
  upload: IconsMap.UPLOAD,
  publish: IconsMap.CREATE_PAGE,
  unpublish: IconsMap.MINUS,
};

const activityActionLocKeys = {
  edit: 'notification_panel.notification_panel-edited',
  comment: 'notification_panel.notification_panel-commented',
  create: 'notification_panel.notification_panel-created',
  mention: 'notification_panel.notification_panel-mentioned',
  message: 'notification_panel.notification_panel-sent_direct_message',
  move: 'notification_panel.notification_panel-moved',
  remove: 'notification_panel.notification_panel-deleted',
  reply: 'notification_panel.notification_panel-replied_your_comment',
  share: 'notification_panel.notification_panel-shared',
  commentMention: 'notification_panel.notification_panel-mentioned_in_comment',
  commentReply: 'notification_panel.notification_panel-comment_replied_your_comment_to_page',
  chatMention: 'notification_panel.notification_panel-mentioned_in_channel',
  chatReply: 'notification_panel.notification_panel-replied_your_message',
  chatDirectMessage: 'notification_panel.notification_panel-chat_direct_message',
  bucketAttachmentUploaded: 'notification_panel.notification_panel-uploaded_file',
  bucketAttachmentRemoved: 'notification_panel.notification_panel-deleted_file',
  publish: 'notification_panel.notification_panel-published',
  unpublish: 'notification_panel.notification_panel-unpublished',
};

/**
 * Get full url with domain
 * @param url
 * @returns
 */
const getUrlWithDomain = (url: string) => {
  if (url.startsWith('/')) {
    return `https://${document.location.host}${url}`;
  }
  return url;
};

export function ActivityItem({
  itemToShow,
  assetsPrefixUrl,
  activitiesCategory,
  gatewayUrl,
  sessionId,
  userId,
  fetchChatActivities = true,
  portalContext,
  mode = 'webnotes',
  routerPush,
  openCommentsByThreadId,
  onClose,
  selectMenuItem,
  showFileInDashboard,
}: {
  itemToShow: NotificationActivityEnrichedRecord;
  assetsPrefixUrl: string;
  activitiesCategory: string;
  gatewayUrl: string;
  sessionId: string;
  userId: number;
  fetchChatActivities?: boolean;
  portalContext?: NotificationsPortalContext;
  mode: AtivityItemMode;
  routerPush: (url: string) => void;
  openCommentsByThreadId?: (threadId: string, commentId: string) => void;
  onClose: () => void;
  selectMenuItem?: (itemId: string) => void;
  showFileInDashboard?: (globalId: string) => void;
}) {
  const [readActivity, clearBreadcrumbsSpecialPagesVisited] = useUnit([
    readActivityEvent,
    clearBreadcrumbsSpecialPagesVisitedEvent,
  ]);
  const t = useTranslations();

  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
  }, []);

  const { activityIcon, activityActionTextLocKey, activityIconBgColor, activityIconBorderColor, activityIconColor } =
    useMemo(() => {
      let icon: ReactElement | null = null;
      let textLocKey = '';
      let bgColor = '';
      let borderColor = '';
      let iconColor = '';

      const { objectType, action, type } = itemToShow;

      if (itemToShow.action) {
        borderColor = activityIconBorderColorValues[itemToShow.action];
        bgColor = activityIconBgColorValues[itemToShow.action];
        iconColor = activityIconColorValues[itemToShow.action];

        if (type === 'folder' && action === 'create') {
          icon = activityIconValues.createFolder;
        } else {
          icon = activityIconValues[action];
        }

        if (itemToShow.action === 'comment' && itemToShow.comment?.mentioned) {
          textLocKey = activityActionLocKeys.commentMention;
        } else if (itemToShow.action === 'comment' && itemToShow.comment?.replyTo) {
          textLocKey = activityActionLocKeys.commentReply;
        } else if (itemToShow.action === 'move' && itemToShow.extra.noteParentId !== 'trash') {
          textLocKey = activityActionLocKeys.move;
          borderColor = activityIconBorderColorValues.move;
          bgColor = activityIconBgColorValues.move;
          iconColor = activityIconColorValues.move;
          icon = activityIconValues.move;
        } else if (itemToShow.action === 'move' && itemToShow.extra.noteParentId === 'trash') {
          textLocKey = activityActionLocKeys.remove;
          borderColor = activityIconBorderColorValues.remove;
          bgColor = activityIconBgColorValues.remove;
          iconColor = activityIconColorValues.remove;
          icon = activityIconValues.remove;
        } else if (itemToShow.action === 'remove') {
          textLocKey = activityActionLocKeys.remove;
          borderColor = activityIconBorderColorValues.remove;
          bgColor = activityIconBgColorValues.remove;
          iconColor = activityIconColorValues.remove;
          icon = activityIconValues.remove;
        } else {
          textLocKey = activityActionLocKeys[itemToShow.action];
        }

        // Portal page
        if (objectType === 'note') {
          if (action === 'publish') {
            textLocKey = activityActionLocKeys.publish;
          }
          if (action === 'unpublish') {
            textLocKey = activityActionLocKeys.unpublish;
          }
        }

        // Chat
        if (objectType === 'chat') {
          if (action === 'mention') {
            textLocKey = activityActionLocKeys.chatMention;
          }

          if (action === 'reply') {
            textLocKey = activityActionLocKeys.chatReply;
          }

          if (action === 'message') {
            textLocKey = activityActionLocKeys.chatDirectMessage;
          }
        }

        // BucketAttachment
        if (objectType === 'bucketAttachment') {
          if (action === 'create') {
            textLocKey = activityActionLocKeys.bucketAttachmentUploaded;
            icon = activityIconValues.upload;
          }

          if (action === 'remove') {
            textLocKey = activityActionLocKeys.bucketAttachmentRemoved;
          }
        }
      }

      return {
        activityIcon: icon,
        activityActionTextLocKey: textLocKey,
        activityIconBgColor: bgColor,
        activityIconBorderColor: borderColor,
        activityIconColor: iconColor,
      };
    }, [itemToShow]);

  const openPortalPage = useCallback(
    (url: string) => {
      if (mode === 'webnotes') {
        window.open(url, '_blank');
      } else if (mode === 'portals') {
        routerPush(url);
      }
    },
    [mode, routerPush]
  );

  const handleReadActivity = useCallback(
    (read: boolean) => {
      readActivity({
        hostUrl: gatewayUrl,
        workspaceId: itemToShow.workspaceId,
        sessionId,
        globalId: itemToShow.globalId,
        activityId: itemToShow.userActivityStreamItemGlobalId,
        read,
        userId,
        fetchChatActivities,
        portalContext,
      });
    },
    [
      gatewayUrl,
      itemToShow.workspaceId,
      sessionId,
      itemToShow.globalId,
      itemToShow.userActivityStreamItemGlobalId,
      userId,
      fetchChatActivities,
      portalContext,
      readActivity,
    ]
  );

  const handleUnreadActivity = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleReadActivity(!itemToShow.userActivityStreamItemRead);
    },
    [itemToShow.userActivityStreamItemRead, handleReadActivity]
  );

  const getUserAvatar = useMemo(() => {
    if (itemToShow.avatar?.storedFileUUID) {
      const avatar = getBoxFileUrl(itemToShow.avatar?.storedFileUUID, {
        width: 72,
        height: 72,
      });
      if (avatar && typeof avatar === 'string') {
        return <img src={avatar} width={36} height={36} className="w-full h-full rounded-full object-fill" alt="" />;
      }
    } else {
      let userLetter = '';

      if (itemToShow.user?.lastname && itemToShow.user?.firstname) {
        userLetter =
          itemToShow.user?.firstname.slice(0, 1).toUpperCase() + itemToShow.user?.lastname.slice(0, 1).toUpperCase();
      } else if (itemToShow.user?.username) {
        userLetter = itemToShow.user?.username.slice(0, 2).toUpperCase();
      }
      return (
        <div className="bg-white border border-solid border-neutral-200 flex justify-center w-[36px] h-[36px] items-center text-center rounded-full">
          <span className="text-neutral-800 text-[13px] font-medium object-center">{userLetter}</span>
        </div>
      );
    }
  }, [itemToShow]);

  const {
    title,
    url,
    itemDisabled,
    itemParentTitle,
    parentDisabled,
    parentIcon,
    parentUrl,
    contextUrl,
    context,
    openPortalPageInNewTab,
    openPortalRootInNewTab,
    actionRootTitle,
    actionRootUrl,
  } = useMemo(
    () =>
      getActivityParams(
        itemToShow,
        {
          mode,
          portalDomain: portalContext?.settings.domain,
          portalName: portalContext?.settings.name,
        },
        t
      ),
    [itemToShow, portalContext, mode, t]
  );

  const openUrl = useCallback(
    (params: { e: React.MouseEvent; url: string; disabled: boolean; readActivity: boolean; isRoot?: boolean }) => {
      const { e, url, disabled, readActivity, isRoot = false } = params;
      e.stopPropagation();
      if (disabled) {
        return;
      }
      readActivity && handleReadActivity(true);

      const isOpenInNewTab = e.metaKey || e.ctrlKey;
      if (isOpenInNewTab) {
        window.open(url, '_blank');
        return;
      }

      const { note, objectType, comment, portalId } = itemToShow;
      if (
        ((note?.isSharedForPortal || portalId) && openPortalPageInNewTab) ||
        (isRoot && portalId && openPortalRootInNewTab)
      ) {
        openPortalPage(url);
      } else {
        routerPush(url);
      }

      if (readActivity) {
        // Open thread in page
        if (objectType === 'comment' && comment) {
          if (openCommentsByThreadId) {
            try {
              const { pathname } = new URL(getUrlWithDomain(url));
              if (pathname === document.location.pathname) {
                openCommentsByThreadId(comment.threadId, comment.globalId);
              }
            } catch (err) {
              console.error(err);
            }
          }
        }

        // Open detailed task in portal
        if ((objectType === 'task' || (objectType === 'comment' && itemToShow.task)) && mode === 'portals') {
          itemToShow.task && openTaskDetailedInDashboard(itemToShow.task.globalId);
        }

        // Open file in dashboard
        if (objectType === 'bucketAttachment' && showFileInDashboard) {
          showFileInDashboard(itemToShow.objectId);
        }
      }

      clearBreadcrumbsSpecialPagesVisited();
      if (selectMenuItem && itemToShow.nodeId) {
        selectMenuItem(itemToShow.nodeId);
      }

      onClose();
    },
    [
      itemToShow,
      openPortalPageInNewTab,
      openPortalRootInNewTab,
      mode,
      openPortalPage,
      routerPush,
      onClose,
      handleReadActivity,
      openCommentsByThreadId,
      selectMenuItem,
      showFileInDashboard,
      clearBreadcrumbsSpecialPagesVisited,
    ]
  );

  return (
    <div
      data-cy="notif-activity-card"
      className={classNames(
        'w-full py-4 px-5 flex items-start gap-3 self-stretch border-b border-b-solid border-neutral-200 transition-colors duration-200 hover:bg-neutral-100 focus:bg-neutral-100',
        {
          'cursor-pointer': !itemDisabled,
        }
      )}
      onClick={(e) => openUrl({ e, url, disabled: itemDisabled, readActivity: true })}
    >
      <div className="w-[40px] h-[36px] flex items-center justify-center rounded-full relative">
        {getUserAvatar}
        {activityIcon && activityIconBgColor && (
          <ActivityIcon
            background={activityIconBgColor}
            icon={activityIcon}
            iconColor={activityIconColor}
            borderColor={activityIconBorderColor}
          />
        )}
      </div>
      <div className="flex items-start gap-2 w-full justify-between">
        <div className="flex flex-col items-start gap-1 max-w-[311px] flex-wrap">
          <div className="flex items-start mb-1 w-full flex-wrap text-sm">
            <div data-cy="notif-activity-user" className="font-semibold text-neutral-800 mr-1">
              {getUserName(itemToShow.user)}
            </div>
            <div className="text-neutral-800 mr-1">{t(activityActionTextLocKey)}</div>
            <ActivityTitle
              title={title}
              isCursorDefault={itemDisabled}
              onClick={
                mode === 'portals' && isMobile
                  ? () => null
                  : (e) => openUrl({ e, url, disabled: itemDisabled, readActivity: true })
              }
            />
          </div>

          <div
            data-cy="notif-activity-breadcrumbs"
            className="flex max-w-[342px] items-center gap-1 text-[10px] leading-3"
          >
            <ActivityRoot
              title={actionRootTitle}
              fullWidth={itemParentTitle ? false : true}
              onClick={
                mode === 'portals' && isMobile
                  ? () => null
                  : (e) => openUrl({ e, url: actionRootUrl ?? '/', disabled: false, readActivity: false, isRoot: true })
              }
            />
            {itemParentTitle ? (
              <>
                <div className="text-neutral-400 text-[10px] font-semibold ">•</div>
                <ActivityParent
                  title={itemParentTitle}
                  icon={parentIcon}
                  disabled={parentDisabled}
                  onClick={
                    mode === 'portals' && isMobile
                      ? () => null
                      : (e) => openUrl({ e, url: parentUrl, disabled: parentDisabled, readActivity: false })
                  }
                />
              </>
            ) : null}
          </div>

          {context ? (
            <ActivityContext
              disabled={itemDisabled}
              text={context}
              onClick={
                mode === 'portals' && isMobile
                  ? () => null
                  : (e) => openUrl({ e, url: contextUrl, disabled: false, readActivity: true })
              }
            />
          ) : null}
        </div>
        <div className="flex gap-2 py-[3px] items-center max-w-[87px] whitespace-nowrap">
          <div className="text-neutral-400 text-[12px] leading-4">{itemToShow.actionTime}</div>

          {activitiesCategory === 'important' ? (
            <Tooltip
              message={t('notification_panel.notification_panel-mark_as_read_tooltip')}
              position={UiTooltipPosition.TOP_LEFT_SM}
            >
              <div
                data-cy="notif-card-mark"
                className="relative cursor-pointer w-[14px] h-[14px] flex items-center justify-center lg:before:hidden before:block before:absolute before:w-[42px] before:h-[42px]"
                onClick={handleUnreadActivity}
              >
                <Icon
                  icon={IconsMap.CIRCLE}
                  className={classNames('w-2 h-2   rounded-full', {
                    'bg-teal-500 text-teal-500': !itemToShow.userActivityStreamItemRead,
                    'bg-neutral-300 text-neutral-300': itemToShow.userActivityStreamItemRead,
                  })}
                />
              </div>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}
